/**
 * discountHelper.ts — Cálculo de descuentos y ajustes de precio en caja.
 *
 * Exporta funciones puras; no UI ni storage.
 * Los tipos LineAdjustment/LineAdjustmentMode viven en `@/types` (los usa
 * también CartItem a nivel global) y se re-exportan aquí para compatibilidad.
 */
import type { LineAdjustment } from "@/types";

export type { LineAdjustment, LineAdjustmentMode } from "@/types";

export type DiscountType = "%" | "$";

export interface LineDiscount {
  type: DiscountType;
  value: number;
}

export interface CartDiscounts {
  byItem: Record<string, LineDiscount>;
  global: LineDiscount | null;
  lineAdjustments: Record<string, LineAdjustment>;
}

export interface LossWarning {
  hasLoss: boolean;
  amount: number;
  thresholdPercent: number | null;
}

export interface PersistedLineAdjustmentSummary {
  lineId: string;
  nombre: string;
  bruto: number;
  netoAntesGlobal: number;
  netoFinal: number;
  descuentoItem: number;
  descuentoGlobalAsignado: number;
  descuentoTotalLinea: number;
  cantidad?: number;
  precioUnitarioOriginal?: number;
  precioUnitarioFinal?: number;
  ajusteAplicado?: LineAdjustment | null;
}

export interface DiscountBreakdownLine {
  lineId: string;
  nombre: string;
  bruto: number;
  netoAntesGlobal: number;
  descuentoItem: number;
  netoItem: number;
  descuentoGlobalAsignado: number;
  descuentoTotalLinea: number;
  ajusteAplicado: LineAdjustment | null;
  precioUnitarioOriginal?: number;
  precioUnitarioFinal?: number;
  cantidad?: number;
}

export interface DiscountBreakdown {
  subtotalBruto: number;
  totalDescuentoItems: number;
  descuentoGlobal: number;
  total: number;
  lines: DiscountBreakdownLine[];
}

const clampMoney = (value: number): number => Math.max(0, Math.round(value));

const applyDiscount = (base: number, d: LineDiscount | null): number => {
  if (!d || d.value <= 0) return clampMoney(base);
  if (d.type === "%") {
    return clampMoney(base * (1 - Math.min(100, d.value) / 100));
  }
  return clampMoney(base - d.value);
};

const resolveLineNetFromAdjustment = (
  bruto: number,
  quantity: number,
  adjustment: LineAdjustment | null,
): number => {
  if (!adjustment) return clampMoney(bruto);

  if (adjustment.mode === "discountPercent") {
    return applyDiscount(bruto, {
      type: "%",
      value: Math.max(0, adjustment.discountPercent ?? 0),
    });
  }

  if (adjustment.mode === "manualPrice") {
    const unit = Math.max(0, adjustment.manualUnitPrice ?? 0);
    return clampMoney(unit * Math.max(1, quantity));
  }

  const costUnit = Math.max(0, adjustment.costUnitPrice ?? 0);
  return clampMoney(costUnit * Math.max(1, quantity));
};

export const emptyCartDiscounts = (): CartDiscounts => ({
  byItem: {},
  global: null,
  lineAdjustments: {},
});

export const calcLossWarning = (
  baseUnitPrice: number,
  targetUnitPrice: number,
  costUnitPrice?: number | null,
): LossWarning => {
  const safeBase = Math.max(0, Math.round(baseUnitPrice || 0));
  const safeTarget = Math.max(0, Math.round(targetUnitPrice || 0));
  const safeCost = Math.max(0, Math.round(costUnitPrice || 0));
  if (safeBase <= 0 || safeCost <= 0) {
    return { hasLoss: false, amount: 0, thresholdPercent: null };
  }

  const thresholdPercent = Math.max(
    0,
    Math.floor((1 - safeCost / safeBase) * 100),
  );

  if (safeTarget >= safeCost) {
    return { hasLoss: false, amount: 0, thresholdPercent };
  }

  return {
    hasLoss: true,
    amount: safeCost - safeTarget,
    thresholdPercent,
  };
};

export const buildLineAdjustmentLabel = (
  line: Pick<
    PersistedLineAdjustmentSummary,
    "ajusteAplicado" | "precioUnitarioOriginal" | "precioUnitarioFinal"
  >,
): string | undefined => {
  const adj = line.ajusteAplicado;
  if (!adj) return undefined;

  if (adj.mode === "discountPercent") {
    const pct = Math.max(0, Math.round(adj.discountPercent ?? 0));
    return pct > 0 ? `Descuento ${pct}%` : undefined;
  }

  if (adj.mode === "manualPrice") {
    const before = Math.max(0, Math.round(line.precioUnitarioOriginal ?? 0));
    const after = Math.max(
      0,
      Math.round(adj.manualUnitPrice ?? line.precioUnitarioFinal ?? 0),
    );
    if (before > 0 && after > 0 && before !== after) {
      return `Precio manual ${before.toLocaleString("es-CO")}→${after.toLocaleString("es-CO")}`;
    }
    return "Precio manual";
  }

  const cost = Math.max(
    0,
    Math.round(adj.costUnitPrice ?? line.precioUnitarioFinal ?? 0),
  );
  return cost > 0
    ? `Precio costo ${cost.toLocaleString("es-CO")}`
    : "Precio costo";
};

export function calculateDiscountBreakdown(
  items: Array<{
    lineId: string;
    nombre: string;
    bruto: number;
    quantity?: number;
    precioUnitarioOriginal?: number;
    /** Neto real cobrado por la línea (el carrito ya resolvió el precio). */
    netoReal?: number;
  }>,
  discounts: CartDiscounts,
): DiscountBreakdown {
  const baseLines: DiscountBreakdownLine[] = items.map((it) => {
    const quantity = Math.max(1, Math.round(it.quantity ?? 1));
    const itemDisc = discounts.byItem[it.lineId] ?? null;
    const lineAdjustment = discounts.lineAdjustments[it.lineId] ?? null;
    // El carrito es la fuente de verdad del cobrado: si trae el neto real no se
    // re-aplican ajustes sobre la lista (los descuentos especiales/ítem ya
    // quedaron resueltos en el precio de la línea).
    const adjustedNeto =
      typeof it.netoReal === "number"
        ? clampMoney(it.netoReal)
        : resolveLineNetFromAdjustment(it.bruto, quantity, lineAdjustment);
    const discountedNeto = applyDiscount(adjustedNeto, itemDisc);
    const precioUnitarioFinal =
      quantity > 0 ? clampMoney(discountedNeto / quantity) : undefined;

    return {
      lineId: it.lineId,
      nombre: it.nombre,
      bruto: clampMoney(it.bruto),
      netoAntesGlobal: discountedNeto,
      descuentoItem: Math.max(0, clampMoney(it.bruto) - discountedNeto),
      netoItem: discountedNeto,
      descuentoGlobalAsignado: 0,
      descuentoTotalLinea: Math.max(0, clampMoney(it.bruto) - discountedNeto),
      ajusteAplicado: lineAdjustment,
      precioUnitarioOriginal: it.precioUnitarioOriginal,
      precioUnitarioFinal,
      cantidad: quantity,
    };
  });

  const subtotalBruto = baseLines.reduce((s, l) => s + l.bruto, 0);
  const totalDescuentoItems = baseLines.reduce(
    (s, l) => s + l.descuentoItem,
    0,
  );
  const subtotalNetoItems = baseLines.reduce(
    (s, l) => s + l.netoAntesGlobal,
    0,
  );
  const afterGlobal = applyDiscount(subtotalNetoItems, discounts.global);
  const descuentoGlobal = Math.max(0, subtotalNetoItems - afterGlobal);

  if (descuentoGlobal <= 0 || subtotalNetoItems <= 0) {
    return {
      subtotalBruto,
      totalDescuentoItems,
      descuentoGlobal,
      total: afterGlobal,
      lines: baseLines,
    };
  }

  let assignedGlobal = 0;
  const lines = baseLines.map((line, index) => {
    const isLast = index === baseLines.length - 1;
    const proportional = isLast
      ? descuentoGlobal - assignedGlobal
      : Math.round(
          (line.netoAntesGlobal / subtotalNetoItems) * descuentoGlobal,
        );
    const safeAssigned = Math.max(
      0,
      Math.min(line.netoAntesGlobal, proportional),
    );
    assignedGlobal += safeAssigned;
    const netoItem = Math.max(0, line.netoAntesGlobal - safeAssigned);
    return {
      ...line,
      descuentoGlobalAsignado: safeAssigned,
      netoItem,
      descuentoTotalLinea: line.descuentoItem + safeAssigned,
    };
  });

  const diff = afterGlobal - lines.reduce((s, l) => s + l.netoItem, 0);
  if (diff !== 0 && lines.length > 0) {
    const last = lines[lines.length - 1];
    lines[lines.length - 1] = {
      ...last,
      netoItem: Math.max(0, last.netoItem + diff),
      descuentoGlobalAsignado: Math.max(0, last.descuentoGlobalAsignado - diff),
      descuentoTotalLinea: Math.max(
        0,
        last.bruto - Math.max(0, last.netoItem + diff),
      ),
    };
  }

  return {
    subtotalBruto,
    totalDescuentoItems,
    descuentoGlobal,
    total: afterGlobal,
    lines,
  };
}

export const toPersistedLineAdjustments = (
  lines: DiscountBreakdownLine[],
): PersistedLineAdjustmentSummary[] =>
  lines
    .filter((line) => line.descuentoTotalLinea > 0 || line.ajusteAplicado)
    .map((line) => ({
      lineId: line.lineId,
      nombre: line.nombre,
      bruto: line.bruto,
      netoAntesGlobal: line.netoAntesGlobal,
      netoFinal: line.netoItem,
      descuentoItem: line.descuentoItem,
      descuentoGlobalAsignado: line.descuentoGlobalAsignado,
      descuentoTotalLinea: line.descuentoTotalLinea,
      cantidad: line.cantidad,
      precioUnitarioOriginal: line.precioUnitarioOriginal,
      precioUnitarioFinal: line.precioUnitarioFinal,
      ajusteAplicado: line.ajusteAplicado,
    }));
